/** Screens whose prompts have not run yet: a titled page with a "Coming in x.y.z" card. */
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Later, PageHead } from '@/components/Later';
import { SettingsControls } from '@/components/SettingsControls';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { Me } from '@/auth/useMe';
import { SIGN_OUT_URL } from '@/auth/useMe';
import { clearTodayCopy } from '@/lib/today-cache';
import { replayWelcome } from '@/components/Welcome';
import '@/styles/admin1025.css';
import { UpdateLine } from '@/components/PwaUpdate';
import { CallMeField } from '@/pages/house/CallMe';
import { VisitSettingsField } from '@/features/tech/visit/VisitSettingsField';
import { TicketEmailsField } from '@/features/tech/TicketEmailsField';

export function LaterPage({ titleKey, bodyKey, version }: { titleKey: string; bodyKey: string; version: string }) {
  const { t } = useTranslation();
  return (
    <section aria-labelledby="h-page">
      <PageHead id="h-page" title={t(titleKey)} />
      <Later version={version} body={t(bodyKey)} />
    </section>
  );
}

/**
 * /settings — My settings (1.5.16 §7), the person's own page, for every role: who they are (their name, the
 * account they are signed in with and their role in words — read-only), language · appearance · text size and
 * "Hide my number when I call from the app" (these follow the person: src/lib/my-settings.ts, src/lib/calling.ts),
 * this device's own choices, and "Replay the welcome". Nothing here goes to Admin.
 */
export function SettingsPage({ me }: { me: Me }) {
  const { t } = useTranslation();
  return (
    <section aria-labelledby="h-settings">
      <PageHead id="h-settings" title={t('mine.title')} />
      <div className="grid-2">
        <Card aria-labelledby="h-who">
          <CardHeader>
            <CardTitle id="h-who">{t('mine.who')}</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="kv" data-testid="mine-who">
              <dt>{t('mine.name')}</dt>
              <dd data-testid="mine-name">{me.name}</dd>
              <dt>{t('mine.account')}</dt>
              <dd data-testid="mine-account">{me.principal?.userDetails}</dd>
              <dt>{t('mine.role')}</dt>
              <dd data-testid="mine-role">{me.role ? t(`roles.${me.role}`) : '—'}</dd>
            </dl>
            <p className="help" style={{ marginTop: 10 }}>
              {t('mine.whoNote')}
            </p>
            <p style={{ marginTop: 14, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button type="button" className="btn secondary sm" onClick={(e) => replayWelcome(e.currentTarget)} title={t('mine.replayHelp')} data-testid="replay-welcome">
                {t('settings.replayWelcome')}
              </button>
              <Link className="btn secondary sm" to="/install" data-testid="install-link">
                {t('install.link')}
              </Link>
              <Link className="btn secondary sm" to="/help" data-testid="help-link">
                {t('help.link')}
              </Link>
              <a className="btn secondary sm" href={SIGN_OUT_URL} onClick={clearTodayCopy}>
                {t('settings.signOut')}
              </a>
            </p>
            <p className="help" style={{ marginTop: 14 }}>
              {t('app.version', { version: `${__APP_RELEASE__} · ${__APP_VERSION__.slice(0, 7)}` })}
            </p>
            {/* 1.6.5: for every role — a crew phone is where it matters: "App 1.6.4 · the site has 1.6.5 · Update now" */}
            <p className="help" style={{ marginTop: 6 }} data-testid="mine-update">
              <UpdateLine />
            </p>
          </CardContent>
        </Card>
        <Card aria-labelledby="h-own">
          <CardHeader>
            <CardTitle id="h-own">{t('mine.settings')}</CardTitle>
          </CardHeader>
          {/* 1.5.22: the page's own class — never the gear pop-over's (.settings-pop), which is a scroller sized to the screen */}
          <CardContent className="settings-page-card" data-testid="mine-settings-card">
            <SettingsControls />
            {/* 1.8.4 (D-1.8.4-3): the name Today greets the person with — kept on the server, so it follows them */}
            <CallMeField />
            {/* 1.8.5 (D-1.8.5-3): the tech's own "Email me about new tickets" */}
            {me.role === 'tech' ? <TicketEmailsField /> : null}
            {['tech', 'admin', 'coadmin'].includes(me.role ?? '') ? (
              <div className="mine-visit" data-testid="mine-visit">
                <VisitSettingsField />
              </div>
            ) : null}
            <p className="help">{t('mine.follows')}</p>
            <p className="help">{t('mine.device')}</p>
            <p className="help">{t('settings.note')}</p>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}

export function NoAccessPage() {
  const { t } = useTranslation();
  return (
    <section aria-labelledby="h-noaccess">
      <PageHead id="h-noaccess" title={t('noAccess.title')} />
      <Card>
        <CardContent>
          <p>{t('noAccess.body')}</p>
          <p style={{ marginTop: 14, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Link className="btn secondary sm" to="/">
              {t('noAccess.home')}
            </Link>
            <a className="btn secondary sm" href={SIGN_OUT_URL} onClick={clearTodayCopy}>
              {t('settings.signOut')}
            </a>
          </p>
        </CardContent>
      </Card>
    </section>
  );
}

export function NotFoundPage() {
  const { t } = useTranslation();
  return (
    <section aria-labelledby="h-404">
      <PageHead id="h-404" title={t('notFound.title')} />
      <Card>
        <CardContent>
          <p>{t('notFound.body')}</p>
          <p style={{ marginTop: 14 }}>
            <Link className="btn secondary sm" to="/">
              {t('notFound.home')}
            </Link>
          </p>
        </CardContent>
      </Card>
    </section>
  );
}

/** /link/:token — the public, signed one-time link pages (tenant, volunteer, RSVP). Built in 0.6.0, 1.1.0, 1.3.0. */
